import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { getLesson } from '../data/lessons';
import { getVideos } from '../data/videos';
import VideoEmbed from '../components/VideoEmbed';
import {
  getConceptProgress,
  saveLessonStep,
  markLessonComplete,
} from '../data/progress';
import { getConcept, getUnit } from '../data/units';

/* ─── Concept Diagram Support ─────────────────────────────────────────────── */

const DIAGRAM_CONCEPTS = [
  'position-displacement',
  'velocity-speed',
  'acceleration',
  'free-body-diagrams',
  'newtons-third-law',
  'inclined-planes',
  'work',
  'conservation-of-energy',
  'impulse',
  'torque',
  'angular-momentum',
];

function drawDiagram(ctx: CanvasRenderingContext2D, conceptId: string, accentColor: string) {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  ctx.clearRect(0, 0, W, H);

  const drawArrow = (x1: number, y1: number, x2: number, y2: number, color: string, lineWidth = 2) => {
    const headLen = 10;
    const angle = Math.atan2(y2 - y1, x2 - x1);
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  };

  ctx.font = '13px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  switch (conceptId) {
    case 'position-displacement': {
      // Number line with position dot and displacement arrow
      const y = H / 2;
      ctx.strokeStyle = '#999';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(50, y);
      ctx.lineTo(450, y);
      ctx.stroke();
      // Ticks
      for (let i = 0; i <= 8; i++) {
        const x = 50 + i * 50;
        ctx.beginPath();
        ctx.moveTo(x, y - 6);
        ctx.lineTo(x, y + 6);
        ctx.stroke();
        ctx.fillStyle = '#666';
        ctx.fillText(`${i}`, x, y + 20);
      }
      // Position x_i
      const xi = 150, xf = 350;
      ctx.fillStyle = accentColor;
      ctx.beginPath();
      ctx.arc(xi, y, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillText('x\u1D62', xi, y - 22);
      // Position x_f
      ctx.beginPath();
      ctx.arc(xf, y, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillText('x_f', xf, y - 22);
      // Displacement arrow
      drawArrow(xi, y - 40, xf, y - 40, accentColor, 2.5);
      ctx.fillStyle = accentColor;
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('\u0394x = x_f \u2212 x\u1D62', (xi + xf) / 2, y - 56);
      break;
    }
    case 'velocity-speed': {
      // Velocity vector vs speed scalar
      const cy = H / 2;
      // Velocity arrow (vector)
      drawArrow(60, cy - 30, 260, cy - 30, accentColor, 3);
      ctx.fillStyle = accentColor;
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('v\u20D7 = 5 m/s East (velocity)', 250, cy - 52);
      // Speed (scalar)
      ctx.fillStyle = '#e67e22';
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('|v| = 5 m/s (speed \u2014 magnitude only)', 250, cy + 30);
      ctx.strokeStyle = '#e67e22';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(60, cy + 10);
      ctx.lineTo(260, cy + 10);
      ctx.stroke();
      ctx.setLineDash([]);
      // Labels
      ctx.fillStyle = '#555';
      ctx.font = '12px sans-serif';
      ctx.fillText('Vector: has direction', 400, cy - 30);
      ctx.fillText('Scalar: magnitude only', 400, cy + 10);
      break;
    }
    case 'acceleration': {
      // Two velocity arrows and delta-v
      const cy = H / 2;
      // v_i
      drawArrow(40, cy - 40, 160, cy - 40, '#3498db', 3);
      ctx.fillStyle = '#3498db';
      ctx.font = 'bold 13px sans-serif';
      ctx.fillText('v\u1D62', 100, cy - 58);
      // v_f (longer)
      drawArrow(40, cy, 240, cy, '#2ecc71', 3);
      ctx.fillStyle = '#2ecc71';
      ctx.fillText('v_f', 140, cy - 16);
      // delta-v
      drawArrow(300, cy - 20, 420, cy - 20, accentColor, 3);
      ctx.fillStyle = accentColor;
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('\u0394v = v_f \u2212 v\u1D62', 360, cy - 42);
      // a label
      ctx.fillStyle = '#555';
      ctx.font = '13px sans-serif';
      ctx.fillText('a = \u0394v / \u0394t', 360, cy + 20);
      break;
    }
    case 'free-body-diagrams': {
      // Box with force arrows
      const cx = W / 2, cy = H / 2;
      const boxW = 60, boxH = 50;
      ctx.fillStyle = '#ecf0f1';
      ctx.strokeStyle = '#bdc3c7';
      ctx.lineWidth = 2;
      ctx.fillRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);
      ctx.strokeRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);
      ctx.fillStyle = '#333';
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('m', cx, cy);
      // Weight (mg down)
      drawArrow(cx, cy + boxH / 2, cx, cy + boxH / 2 + 60, '#e74c3c', 3);
      ctx.fillStyle = '#e74c3c';
      ctx.fillText('mg', cx + 18, cy + boxH / 2 + 40);
      // Normal (N up)
      drawArrow(cx, cy - boxH / 2, cx, cy - boxH / 2 - 60, '#3498db', 3);
      ctx.fillStyle = '#3498db';
      ctx.fillText('N', cx + 15, cy - boxH / 2 - 40);
      // Applied force (F right)
      drawArrow(cx + boxW / 2, cy, cx + boxW / 2 + 70, cy, '#2ecc71', 3);
      ctx.fillStyle = '#2ecc71';
      ctx.fillText('F', cx + boxW / 2 + 50, cy - 15);
      // Friction (f left)
      drawArrow(cx - boxW / 2, cy, cx - boxW / 2 - 60, cy, '#e67e22', 3);
      ctx.fillStyle = '#e67e22';
      ctx.fillText('f', cx - boxW / 2 - 40, cy - 15);
      break;
    }
    case 'newtons-third-law': {
      // Two objects with equal-opposite forces
      const cy = H / 2;
      // Object A
      ctx.fillStyle = '#dfe6e9';
      ctx.strokeStyle = '#b2bec3';
      ctx.lineWidth = 2;
      ctx.fillRect(100, cy - 30, 70, 60);
      ctx.strokeRect(100, cy - 30, 70, 60);
      ctx.fillStyle = '#333';
      ctx.font = 'bold 15px sans-serif';
      ctx.fillText('A', 135, cy);
      // Object B
      ctx.fillStyle = '#dfe6e9';
      ctx.strokeStyle = '#b2bec3';
      ctx.fillRect(310, cy - 30, 70, 60);
      ctx.strokeRect(310, cy - 30, 70, 60);
      ctx.fillStyle = '#333';
      ctx.fillText('B', 345, cy);
      // F_AB (A pushes B to the right)
      drawArrow(170, cy - 15, 310, cy - 15, '#e74c3c', 3);
      ctx.fillStyle = '#e74c3c';
      ctx.font = 'bold 13px sans-serif';
      ctx.fillText('F(A\u2192B)', 240, cy - 30);
      // F_BA (B pushes A to the left)
      drawArrow(310, cy + 15, 170, cy + 15, '#3498db', 3);
      ctx.fillStyle = '#3498db';
      ctx.fillText('F(B\u2192A)', 240, cy + 36);
      // Equal magnitude label
      ctx.fillStyle = '#555';
      ctx.font = '13px sans-serif';
      ctx.fillText('|F(A\u2192B)| = |F(B\u2192A)|', 240, cy + 70);
      break;
    }
    case 'inclined-planes': {
      // Triangle ramp with force decomposition
      const bx = 80, by = 160, rampW = 280, rampH = 130;
      // Ramp triangle
      ctx.strokeStyle = '#7f8c8d';
      ctx.fillStyle = '#ecf0f1';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + rampW, by);
      ctx.lineTo(bx, by - rampH);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // Angle label
      ctx.fillStyle = '#555';
      ctx.font = '13px sans-serif';
      ctx.fillText('\u03B8', bx + rampW - 40, by - 10);
      // Block on ramp
      const blockX = bx + rampW * 0.4;
      const blockY = by - rampH * 0.6;
      ctx.fillStyle = '#bdc3c7';
      ctx.fillRect(blockX - 15, blockY - 15, 30, 30);
      ctx.strokeRect(blockX - 15, blockY - 15, 30, 30);
      // mg down
      drawArrow(blockX, blockY, blockX, blockY + 65, '#e74c3c', 2.5);
      ctx.fillStyle = '#e74c3c';
      ctx.fillText('mg', blockX + 16, blockY + 45);
      // mg sin theta (along ramp, downhill)
      const angle = Math.atan2(rampH, rampW);
      const sinLen = 45;
      drawArrow(blockX, blockY, blockX + sinLen * Math.cos(Math.PI - angle + Math.PI), blockY + sinLen * Math.sin(Math.PI - angle + Math.PI), '#3498db', 2.5);
      ctx.fillStyle = '#3498db';
      ctx.fillText('mg sin\u03B8', blockX - 55, blockY + 25);
      // Normal force
      const nx = -Math.sin(angle), ny = -Math.cos(angle);
      drawArrow(blockX, blockY, blockX + nx * 55, blockY - Math.abs(ny * 55), '#2ecc71', 2.5);
      ctx.fillStyle = '#2ecc71';
      ctx.fillText('N', blockX + nx * 55 - 15, blockY - Math.abs(ny * 55) - 10);
      break;
    }
    case 'work': {
      // Force at angle to displacement
      const cy = H / 2;
      const startX = 80, endX = 380;
      // Displacement arrow
      drawArrow(startX, cy, endX, cy, '#7f8c8d', 2.5);
      ctx.fillStyle = '#7f8c8d';
      ctx.font = 'bold 13px sans-serif';
      ctx.fillText('d', (startX + endX) / 2, cy + 20);
      // Force arrow at angle
      const fAngle = -Math.PI / 6;
      const fLen = 120;
      drawArrow(startX, cy, startX + fLen * Math.cos(fAngle), cy + fLen * Math.sin(fAngle), accentColor, 3);
      ctx.fillStyle = accentColor;
      ctx.fillText('F', startX + fLen * Math.cos(fAngle) + 10, cy + fLen * Math.sin(fAngle) - 5);
      // F cos theta component (horizontal projection)
      ctx.setLineDash([5, 3]);
      ctx.strokeStyle = '#e74c3c';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(startX + fLen * Math.cos(fAngle), cy + fLen * Math.sin(fAngle));
      ctx.lineTo(startX + fLen * Math.cos(fAngle), cy);
      ctx.stroke();
      ctx.setLineDash([]);
      drawArrow(startX, cy - 30, startX + fLen * Math.cos(fAngle), cy - 30, '#e74c3c', 2);
      ctx.fillStyle = '#e74c3c';
      ctx.fillText('F cos\u03B8', startX + (fLen * Math.cos(fAngle)) / 2, cy - 46);
      // Angle arc
      ctx.strokeStyle = '#555';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(startX, cy, 30, fAngle, 0);
      ctx.stroke();
      ctx.fillStyle = '#555';
      ctx.fillText('\u03B8', startX + 38, cy - 12);
      // W = Fd cos theta
      ctx.fillStyle = '#333';
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('W = Fd cos\u03B8', 420, cy - 10);
      break;
    }
    case 'conservation-of-energy': {
      // Bar chart: KE + PE = constant at 3 positions
      const positions = [
        { label: 'Top', ke: 10, pe: 90 },
        { label: 'Mid', ke: 50, pe: 50 },
        { label: 'Bottom', ke: 90, pe: 10 },
      ];
      const barW = 50, maxH = 120, gap = 100;
      const startX = 80;
      const baseY = 170;
      positions.forEach((pos, i) => {
        const x = startX + i * (barW + gap);
        const keH = (pos.ke / 100) * maxH;
        const peH = (pos.pe / 100) * maxH;
        // PE bar (stacked on top of KE)
        ctx.fillStyle = '#3498db';
        ctx.fillRect(x, baseY - keH - peH, barW, peH);
        // KE bar
        ctx.fillStyle = '#e74c3c';
        ctx.fillRect(x, baseY - keH, barW, keH);
        // Label
        ctx.fillStyle = '#333';
        ctx.font = '12px sans-serif';
        ctx.fillText(pos.label, x + barW / 2, baseY + 16);
      });
      // Total line
      ctx.strokeStyle = '#2ecc71';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.beginPath();
      ctx.moveTo(startX - 20, baseY - maxH);
      ctx.lineTo(startX + 2 * (barW + gap) + barW + 20, baseY - maxH);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#2ecc71';
      ctx.font = 'bold 12px sans-serif';
      ctx.fillText('Total E = const', startX + (barW + gap) + barW / 2, baseY - maxH - 10);
      // Legend
      ctx.fillStyle = '#e74c3c';
      ctx.fillRect(380, 40, 14, 14);
      ctx.fillStyle = '#333';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('KE', 400, 52);
      ctx.fillStyle = '#3498db';
      ctx.fillRect(380, 62, 14, 14);
      ctx.fillStyle = '#333';
      ctx.fillText('PE', 400, 74);
      ctx.textAlign = 'center';
      break;
    }
    case 'impulse': {
      // F-t graph with area = impulse
      const ox = 80, oy = 150, axisW = 320, axisH = 120;
      // Axes
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(ox, oy - axisH);
      ctx.lineTo(ox, oy);
      ctx.lineTo(ox + axisW, oy);
      ctx.stroke();
      ctx.fillStyle = '#333';
      ctx.font = '13px sans-serif';
      ctx.fillText('t', ox + axisW + 15, oy + 4);
      ctx.save();
      ctx.translate(ox - 20, oy - axisH / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText('F', 0, 0);
      ctx.restore();
      // Force pulse (trapezoid shape)
      const t1 = ox + 60, t2 = ox + 120, t3 = ox + 220, t4 = ox + 280;
      const fH = 80;
      ctx.fillStyle = accentColor + '40';
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(t1, oy);
      ctx.lineTo(t2, oy - fH);
      ctx.lineTo(t3, oy - fH);
      ctx.lineTo(t4, oy);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // Area label
      ctx.fillStyle = accentColor;
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('Area = J = \u0394p', (t2 + t3) / 2, oy - fH / 2 + 5);
      // Impulse equation
      ctx.fillStyle = '#333';
      ctx.font = 'bold 13px sans-serif';
      ctx.fillText('J = F\u0394t = \u0394p', ox + axisW / 2, oy + 30);
      break;
    }
    case 'torque': {
      // Lever arm with r, F, and tau = rF sin theta
      const pivotX = 150, pivotY = H / 2;
      // Pivot point
      ctx.fillStyle = '#333';
      ctx.beginPath();
      ctx.arc(pivotX, pivotY, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillText('pivot', pivotX, pivotY + 22);
      // Lever arm (r)
      const armLen = 180;
      const armAngle = -Math.PI / 12;
      const endArmX = pivotX + armLen * Math.cos(armAngle);
      const endArmY = pivotY + armLen * Math.sin(armAngle);
      ctx.strokeStyle = '#7f8c8d';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(pivotX, pivotY);
      ctx.lineTo(endArmX, endArmY);
      ctx.stroke();
      // r label
      ctx.fillStyle = '#3498db';
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('r', (pivotX + endArmX) / 2, pivotY - 20);
      // Force at angle
      const fAngle = -Math.PI / 3;
      const fLen = 80;
      drawArrow(endArmX, endArmY, endArmX + fLen * Math.cos(fAngle), endArmY + fLen * Math.sin(fAngle), '#e74c3c', 3);
      ctx.fillStyle = '#e74c3c';
      ctx.fillText('F', endArmX + fLen * Math.cos(fAngle) + 12, endArmY + fLen * Math.sin(fAngle));
      // Angle arc
      ctx.strokeStyle = '#555';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(endArmX, endArmY, 25, armAngle + Math.PI, fAngle + Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = '#555';
      ctx.font = '12px sans-serif';
      ctx.fillText('\u03B8', endArmX - 30, endArmY - 18);
      // Equation
      ctx.fillStyle = accentColor;
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('\u03C4 = rF sin\u03B8', 400, pivotY);
      break;
    }
    case 'angular-momentum': {
      // Circle with L = I omega arrow
      const cx = W / 2 - 40, cy = H / 2;
      const radius = 60;
      // Circle (orbit)
      ctx.strokeStyle = '#bdc3c7';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      // Center dot
      ctx.fillStyle = '#333';
      ctx.beginPath();
      ctx.arc(cx, cy, 4, 0, Math.PI * 2);
      ctx.fill();
      // Rotating object
      ctx.fillStyle = accentColor;
      ctx.beginPath();
      ctx.arc(cx + radius, cy, 8, 0, Math.PI * 2);
      ctx.fill();
      // Curved arrow for omega
      ctx.strokeStyle = '#3498db';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 18, -Math.PI / 4, Math.PI / 2);
      ctx.stroke();
      // Arrowhead on curved arrow
      const tipAngle = Math.PI / 2;
      const tipX = cx + (radius + 18) * Math.cos(tipAngle);
      const tipY = cy + (radius + 18) * Math.sin(tipAngle);
      ctx.fillStyle = '#3498db';
      ctx.beginPath();
      ctx.moveTo(tipX, tipY);
      ctx.lineTo(tipX + 8, tipY - 8);
      ctx.lineTo(tipX - 4, tipY - 6);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#3498db';
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('\u03C9', cx + radius + 35, cy - 20);
      // L arrow (out of page / upward)
      drawArrow(cx, cy, cx, cy - 90, accentColor, 3);
      ctx.fillStyle = accentColor;
      ctx.font = 'bold 16px sans-serif';
      ctx.fillText('L', cx + 15, cy - 80);
      // Equation
      ctx.fillStyle = '#333';
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('L = I\u03C9', cx + radius + 100, cy);
      break;
    }
    default:
      break;
  }
}

const ConceptDiagram: React.FC<{ conceptId: string; accentColor: string }> = ({ conceptId, accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    drawDiagram(ctx, conceptId, accentColor);
  }, [conceptId, accentColor]);

  // Only show for concepts that have diagrams
  if (!DIAGRAM_CONCEPTS.includes(conceptId)) return null;

  return (
    <DiagramWrapper>
      <canvas ref={canvasRef} width={500} height={200} style={{ maxWidth: '100%', height: 'auto' }} />
    </DiagramWrapper>
  );
};

const LessonScreen: React.FC = () => {
  const { unitId, conceptId } = useParams<{
    unitId: string;
    conceptId: string;
  }>();
  const navigate = useNavigate();

  const unit = unitId ? getUnit(unitId) : undefined;
  const concept =
    unitId && conceptId ? getConcept(unitId, conceptId) : undefined;
  const lesson = unitId && conceptId ? getLesson(unitId, conceptId) : undefined;
  const videos =
    unitId && conceptId ? getVideos(unitId, conceptId) : [];

  const savedProgress =
    unitId && conceptId ? getConceptProgress(unitId, conceptId) : undefined;

  const [currentStep, setCurrentStep] = useState(0);
  const [fadeKey, setFadeKey] = useState(0);

  // Restore saved step on mount
  useEffect(() => {
    if (savedProgress && savedProgress.lessonStep > 0 && lesson) {
      const restoredStep = Math.min(
        savedProgress.lessonStep,
        lesson.steps.length - 1
      );
      setCurrentStep(restoredStep);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalSteps = lesson?.steps.length ?? 0;
  const stepProgressPct =
    totalSteps > 0 ? Math.round(((currentStep + 1) / totalSteps) * 100) : 0;

  const handleNextStep = useCallback(() => {
    if (!lesson || !unitId || !conceptId) return;
    if (currentStep < lesson.steps.length - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      setFadeKey((k) => k + 1);
      saveLessonStep(unitId, conceptId, next);
    }
  }, [currentStep, lesson, unitId, conceptId]);

  const handlePrevStep = useCallback(() => {
    if (!unitId || !conceptId) return;
    if (currentStep > 0) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      setFadeKey((k) => k + 1);
      saveLessonStep(unitId, conceptId, prev);
    }
  }, [currentStep, unitId, conceptId]);

  const handleComplete = useCallback(() => {
    if (!unitId || !conceptId) return;
    markLessonComplete(unitId, conceptId);
    navigate(`/unit/${unitId}/concept/${conceptId}/quiz`);
  }, [unitId, conceptId, navigate]);

  const handleBack = () => {
    navigate(`/unit/${unitId}`);
  };

  // ── Not found state ──────────────────────────────────────────────────────────
  if (!unit || !concept || !lesson) {
    return (
      <Container>
        <NotFoundWrapper>
          <NotFoundIcon>📚</NotFoundIcon>
          <NotFoundTitle>Lesson coming soon!</NotFoundTitle>
          <NotFoundText>
            We're still building this lesson. Check back later for a full
            step-by-step breakdown of this concept.
          </NotFoundText>
          <BackLink onClick={() => navigate(unitId ? `/unit/${unitId}` : '/')}>
            &larr; Back to {unit?.title ?? 'Units'}
          </BackLink>
        </NotFoundWrapper>
      </Container>
    );
  }

  const accentColor = unit.color;
  const step = lesson.steps[currentStep];

  return (
    <Container>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Header $color={accentColor}>
        <HeaderInner>
          <BackButton onClick={handleBack}>
            <BackArrow>&larr;</BackArrow>
            <span>Back to {unit.title}</span>
          </BackButton>
          <HeaderTitleBlock>
            <UnitBadge $color={accentColor}>
              Unit {unit.number} &middot; {unit.title}
            </UnitBadge>
            <LessonTitle>{lesson.title}</LessonTitle>
          </HeaderTitleBlock>
        </HeaderInner>
      </Header>

      <Main>
        <ContentColumn>
          {/* ── Introduction ────────────────────────────────────────────────── */}
          <Section>
            <SectionLabel $color={accentColor}>Introduction</SectionLabel>
            <IntroText>{lesson.introduction}</IntroText>
          </Section>

          {/* ── Concept Diagram ─────────────────────────────────────────────── */}
          {conceptId && (
            <ConceptDiagram conceptId={conceptId} accentColor={accentColor} />
          )}

          <Divider />

          {/* ── Step-by-step Teaching ───────────────────────────────────────── */}
          <Section>
            <SectionLabel $color={accentColor}>Lesson Steps</SectionLabel>

            <StepProgressArea>
              <StepIndicator>
                Step {currentStep + 1} of {totalSteps}
              </StepIndicator>
              <StepProgressBarTrack>
                <StepProgressBarFill
                  $pct={stepProgressPct}
                  $color={accentColor}
                />
              </StepProgressBarTrack>
            </StepProgressArea>

            <StepCard key={fadeKey}>
              <StepTitle>{step.title}</StepTitle>
              <StepContent>{step.content}</StepContent>

              {step.keyEquation && (
                <EquationBox>
                  <EquationLabel>Key Equation</EquationLabel>
                  <EquationText>{step.keyEquation}</EquationText>
                </EquationBox>
              )}

              {step.tip && (
                <TipBox>
                  <TipIcon>💡</TipIcon>
                  <TipContent>
                    <TipLabel>Tip</TipLabel>
                    <TipText>{step.tip}</TipText>
                  </TipContent>
                </TipBox>
              )}
            </StepCard>

            <StepNavigation>
              <StepNavButton
                onClick={handlePrevStep}
                disabled={currentStep === 0}
                $variant="secondary"
              >
                &larr; Previous
              </StepNavButton>
              <StepNavButton
                onClick={handleNextStep}
                disabled={currentStep >= totalSteps - 1}
                $variant="primary"
                $color={accentColor}
              >
                Next &rarr;
              </StepNavButton>
            </StepNavigation>
          </Section>

          <Divider />

          {/* ── Videos ──────────────────────────────────────────────────────── */}
          {videos.length > 0 && (
            <>
              <Section>
                <SectionLabel $color={accentColor}>
                  Video Explanation{videos.length > 1 ? 's' : ''}
                </SectionLabel>
                <VideoList>
                  {videos.map((video) => (
                    <VideoItem key={video.youtubeId}>
                      <VideoEmbed
                        youtubeId={video.youtubeId}
                        title={video.title}
                      />
                      <VideoMeta>
                        <VideoTitle>{video.title}</VideoTitle>
                        <VideoDetails>
                          <VideoChannel>{video.channel}</VideoChannel>
                          <VideoDot>&middot;</VideoDot>
                          <VideoDuration>
                            {video.durationMinutes} min
                          </VideoDuration>
                        </VideoDetails>
                        {video.description && (
                          <VideoDescription>{video.description}</VideoDescription>
                        )}
                      </VideoMeta>
                    </VideoItem>
                  ))}
                </VideoList>
              </Section>

              <Divider />
            </>
          )}

          {/* ── Summary ─────────────────────────────────────────────────────── */}
          <Section>
            <SectionLabel $color={accentColor}>Summary</SectionLabel>
            <SummaryText>{lesson.summary}</SummaryText>

            {lesson.keyTakeaways && lesson.keyTakeaways.length > 0 && (
              <TakeawaysBox $color={accentColor}>
                <TakeawaysTitle>Key Takeaways</TakeawaysTitle>
                <TakeawaysList>
                  {lesson.keyTakeaways.map((takeaway, idx) => (
                    <TakeawayItem key={idx} $color={accentColor}>
                      {takeaway}
                    </TakeawayItem>
                  ))}
                </TakeawaysList>
              </TakeawaysBox>
            )}
          </Section>

          <Divider />

          {/* ── Simulation Link ──────────────────────────────────────────── */}
          <Section>
            <SimLinkCard
              $color={accentColor}
              onClick={() => navigate(`/unit/${unitId}/concept/${conceptId}/simulation`)}
            >
              <SimLinkIcon>🔬</SimLinkIcon>
              <SimLinkContent>
                <SimLinkTitle>Interactive Simulation Available</SimLinkTitle>
                <SimLinkDescription>
                  See this concept in action with our interactive simulation.
                </SimLinkDescription>
              </SimLinkContent>
              <SimLinkArrow $color={accentColor}>Try the Simulation &rarr;</SimLinkArrow>
            </SimLinkCard>
          </Section>

          <Divider />

          {/* ── Completion ──────────────────────────────────────────────────── */}
          <CompletionSection>
            <CompletionMessage>
              Ready to test your understanding?
            </CompletionMessage>
            <CompleteButton $color={accentColor} onClick={handleComplete}>
              Mark Complete & Continue to Quiz &rarr;
            </CompleteButton>
          </CompletionSection>
        </ContentColumn>
      </Main>
    </Container>
  );
};

export default LessonScreen;

/* ─── Keyframes ───────────────────────────────────────────────────────────── */

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* ─── Styled Components ───────────────────────────────────────────────────── */

const Container = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
`;

/* ── Header ─────────────────────────────────────────────────────────────── */

const Header = styled.header<{ $color: string }>`
  background: linear-gradient(
    135deg,
    ${(props) => props.$color} 0%,
    ${(props) => props.$color}dd 100%
  );
  padding: 24px 24px 32px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 350px;
    height: 350px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }
`;

const HeaderInner = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  padding: 8px 14px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  align-self: flex-start;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const BackArrow = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const HeaderTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const UnitBadge = styled.span<{ $color: string }>`
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const LessonTitle = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.3px;

  @media (max-width: 600px) {
    font-size: 22px;
  }
`;

/* ── Main content ───────────────────────────────────────────────────────── */

const Main = styled.main`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 24px 80px;
  position: relative;
  z-index: 2;
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: -16px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const Section = styled.section`
  padding: 32px 32px;

  @media (max-width: 600px) {
    padding: 24px 20px;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eef0f5;
  margin: 0;
`;

const SectionLabel = styled.h2<{ $color: string }>`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${(props) => props.$color};
  margin: 0 0 16px;
`;

/* ── Introduction ───────────────────────────────────────────────────────── */

const IntroText = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: #374151;
  margin: 0;
  line-height: 1.7;
`;

/* ── Step-by-step ───────────────────────────────────────────────────────── */

const StepProgressArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const StepIndicator = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
`;

const StepProgressBarTrack = styled.div`
  width: 100%;
  height: 6px;
  background: #eef0f5;
  border-radius: 3px;
  overflow: hidden;
`;

const StepProgressBarFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${(props) => props.$pct}%;
  background: ${(props) => props.$color};
  border-radius: 3px;
  transition: width 0.4s ease;
`;

const StepCard = styled.div`
  animation: ${fadeIn} 0.35s ease-out;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StepTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.3;
`;

const StepContent = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #374151;
  margin: 0;
  line-height: 1.7;
  white-space: pre-wrap;
`;

const EquationBox = styled.div`
  background: #eff6ff;
  border: 2px solid #93c5fd;
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EquationLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #2563eb;
`;

const EquationText = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1e40af;
  font-family: 'Georgia', 'Times New Roman', serif;
  letter-spacing: 0.5px;
  line-height: 1.5;

  @media (max-width: 600px) {
    font-size: 17px;
  }
`;

const TipBox = styled.div`
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const TipIcon = styled.span`
  font-size: 20px;
  line-height: 1;
  flex-shrink: 0;
  margin-top: 2px;
`;

const TipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const TipLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #b45309;
`;

const TipText = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #78350f;
  margin: 0;
  line-height: 1.6;
`;

const StepNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  gap: 12px;
`;

const StepNavButton = styled.button<{
  $variant: 'primary' | 'secondary';
  $color?: string;
}>`
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease, opacity 0.2s ease;

  ${(props) =>
    props.$variant === 'primary'
      ? `
    background: ${props.$color || '#2196F3'};
    color: #ffffff;
    border: none;
    box-shadow: 0 2px 8px ${props.$color || '#2196F3'}40;

    &:hover:not(:disabled) {
      filter: brightness(1.1);
      transform: translateY(-1px);
    }
  `
      : `
    background: #ffffff;
    color: #374151;
    border: 1px solid #d1d5db;

    &:hover:not(:disabled) {
      background: #f9fafb;
      transform: translateY(-1px);
    }
  `}

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/* ── Videos ──────────────────────────────────────────────────────────────── */

const VideoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const VideoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const VideoMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const VideoTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.3;
`;

const VideoDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const VideoChannel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
`;

const VideoDot = styled.span`
  font-size: 13px;
  color: #9ca3af;
`;

const VideoDuration = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
`;

const VideoDescription = styled.p`
  font-size: 13px;
  font-weight: 400;
  color: #9ca3af;
  margin: 4px 0 0;
  line-height: 1.5;
`;

/* ── Summary ─────────────────────────────────────────────────────────────── */

const SummaryText = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: #374151;
  margin: 0 0 20px;
  line-height: 1.7;
`;

const TakeawaysBox = styled.div<{ $color: string }>`
  background: ${(props) => props.$color}0a;
  border: 1px solid ${(props) => props.$color}30;
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TakeawaysTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
`;

const TakeawaysList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TakeawayItem = styled.li<{ $color: string }>`
  font-size: 14px;
  font-weight: 400;
  color: #374151;
  line-height: 1.6;
  padding-left: 20px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 9px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => props.$color};
    opacity: 0.7;
  }
`;

/* ── Completion ──────────────────────────────────────────────────────────── */

const CompletionSection = styled.div`
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;

  @media (max-width: 600px) {
    padding: 32px 20px;
  }
`;

const CompletionMessage = styled.p`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
`;

const CompleteButton = styled.button<{ $color: string }>`
  background: ${(props) => props.$color};
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  padding: 14px 32px;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 14px ${(props) => props.$color}40;

  &:hover {
    filter: brightness(1.1);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${(props) => props.$color}50;
  }

  &:active {
    transform: translateY(0);
  }
`;

/* ── Concept Diagram ────────────────────────────────────────────────────── */

const DiagramWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
  padding: 16px;
  background: #f8f9fc;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
`;

/* ── Simulation Link ────────────────────────────────────────────────────── */

const SimLinkCard = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, ${props => props.$color}08, ${props => props.$color}15);
  border: 1px solid ${props => props.$color}30;
  border-left: 4px solid ${props => props.$color};
  border-radius: 12px;
  padding: 16px 20px;
  margin: 24px 0;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.$color}20;
  }
`;

const SimLinkIcon = styled.span`
  font-size: 28px;
  line-height: 1;
  flex-shrink: 0;
`;

const SimLinkContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const SimLinkTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #1a1a2e;
`;

const SimLinkDescription = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.4;
`;

const SimLinkArrow = styled.span<{ $color: string }>`
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.$color};
  white-space: nowrap;
  flex-shrink: 0;
`;

/* ── Not Found ───────────────────────────────────────────────────────────── */

const NotFoundWrapper = styled.div`
  max-width: 480px;
  margin: 120px auto;
  padding: 0 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const NotFoundIcon = styled.span`
  font-size: 48px;
  line-height: 1;
  margin-bottom: 8px;
`;

const NotFoundTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0;
`;

const NotFoundText = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #2196f3;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 0;
  margin-top: 8px;
  transition: color 0.2s ease;

  &:hover {
    color: #1976d2;
    text-decoration: underline;
  }
`;
